import React, { useEffect, useState } from 'react';
import closeIcon from '../../../../../../Assets/images/close-icon.svg';
import { FIELD_MAX_LENGTHS } from '../../../Utils/Constants';
import apiClient from '../../../../../../Config/apiClient';
import Loader from '../../../../../../Context/Loader/Loader';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import ClipLoader from 'react-spinners/ClipLoader';
import './AddRolesModal.scss';

const rolesBaseUrl = process.env.REACT_APP_HS_ROLES_BASE_PATH;

const AddRoleModal = ({ onClose, editData, refreshList }) => {
  const [formData, setFormData] = useState({ role_name: '', permissions: [] });
  const [formErrors, setFormErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [loadingModules, setLoadingModules] = useState(false);

  const isEdit = !!editData;

  useEffect(() => {
    const init = async () => {
      try {
        setLoadingModules(true);
        const permRes = await apiClient.get(`${rolesBaseUrl}/permissions`);
        const permissions = permRes.data?.data ?? permRes.data;
        setModules(permissions || []);

        if (isEdit) {
          const roleRes = await apiClient.get(`${rolesBaseUrl}/${editData.id}`);
          const role = roleRes.data?.data;

          const activeModuleIds = permissions
            .filter(module =>
              module.items.some(item => role.permissions.includes(item.id))
            )
            .map(module => module.id);

          setFormData({
            role_name: role.role_name || '',
            permissions: role.permissions || [],
          });
          setEnabledModules(activeModuleIds);
        }
      } catch (err) {
        showErrorToast('Failed to fetch permission details.');
      } finally {
        setLoadingModules(false);
      }
    };

    init();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const limit = FIELD_MAX_LENGTHS.roleName;
    if (limit && value.length > limit) return;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleModuleCheckbox = (moduleId) => {
    setEnabledModules(prev => {
      if (prev.includes(moduleId)) {
        const updated = prev.filter(id => id !== moduleId);
        const itemIds = modules.find(m => m.id === moduleId)?.items.map(i => i.id) || [];
        setFormData(prevForm => ({
          ...prevForm,
          permissions: prevForm.permissions.filter(pid => !itemIds.includes(pid)),
        }));
        return updated;
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handleItemCheckbox = (itemId) => {
    setFormData(prev => {
      const updated = prev.permissions.includes(itemId)
        ? prev.permissions.filter(pid => pid !== itemId)
        : [...prev.permissions, itemId];
      return { ...prev, permissions: updated };
    });
  };

  const getFieldError = (name, value) => {
    if (name === 'role_name' && !value.trim()) return 'Permission Name is required.';
    return '';
  };

  const validateForm = () => {
    const errors = {};
    Object.entries(formData).forEach(([name, value]) => {
      const error = getFieldError(name, value);
      if (error) errors[name] = error;
    });
    setFormErrors(errors);
    setSubmitted(true);
    return Object.keys(errors).length === 0;
  };

  const showError = (field) => submitted && formErrors[field];

  const handleSave = async () => {
    if (!validateForm()) return;

    if (formData.permissions.length === 0) {
      showErrorToast("Please select at least one permission.");
      return;
    }

    const payload = {
      role_name: formData.role_name.trim(),
      permissions: formData.permissions,
    };

    try {
      setLoading(true);
      if (isEdit) {
        await apiClient.put(`${rolesBaseUrl}/edit/${editData.id}`, payload);
        showSuccessToast('Permission updated successfully!');
      } else {
        await apiClient.post(`${rolesBaseUrl}/add`, payload);
        showSuccessToast('Permission added successfully!');
      }
      refreshList?.();
      onClose();
    } catch (err) {
      showErrorToast(err?.response?.data?.error_data?.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade show addrole-modal">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="er-content">
            <div className="cpf-top">
              <h2>{isEdit ? 'Edit Role' : 'Add Role'}</h2>
              <button className="m-close" onClick={onClose}>
                <img src={closeIcon} alt="Close" />
              </button>
            </div>
            <div className="cprofile-inner">
              <div className="cp-form">
                <div className="row">
                  <div className="col-md-12">
                    <div className="cp-split" style={{ position: 'relative' }}>
                      <label className="fi-label">
                        Add Role <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="role_name"
                        className={`common-input ${showError('role_name') ? 'error' : ''}`}
                        value={formData.role_name}
                        onChange={handleChange}
                        maxLength={FIELD_MAX_LENGTHS.roleName}
                      />
                      {showError('role_name') && (
                        <span className="error-text active">{formErrors.role_name}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-12">
                    <h3 className="per-head">Permissions</h3>
                    <div className="permission-cvr">
                      <div className="permission-inner">
                        {loadingModules ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <ClipLoader size={50} color="grey" />
                          </div>
                        ) : (
                          modules.map(module => {
                            const moduleItemIds = module.items.map(i => i.id);
                            const isModuleEnabled = enabledModules.includes(module.id);

                            return (
                              <div className="checkbox-group" key={module.id}>
                                <div className="ccheck-cvr">
                                  <input
                                    type="checkbox"
                                    className="common-check"
                                    id={`module-${module.id}`}
                                    checked={isModuleEnabled}
                                    onChange={() => handleModuleCheckbox(module.id)}
                                  />
                                  <label htmlFor={`module-${module.id}`} className="mod-label">
                                    {module.module_name}
                                  </label>
                                </div>
                                <div className="child-checkboxes ms-4">
                                  {module.items.map(item => (
                                    <div className={`ccheck-cvr ${!isModuleEnabled ? 'disabled' : ''}`} key={item.id}>
                                      <input
                                        type="checkbox"
                                        className="common-check"
                                        id={`item-${item.id}`}
                                        checked={formData.permissions.includes(item.id)}
                                        disabled={!isModuleEnabled}
                                        onChange={() => handleItemCheckbox(item.id)}
                                      />
                                      <label htmlFor={`item-${item.id}`}>{item.item_name}</label>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="m-button">
                  <button className="cp-button save-btn" onClick={handleSave} disabled={loading}>
                    {loading ? <Loader /> : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRoleModal;
